import {makeStyles} from "@material-ui/core";
import {Colors, lightGray, secondaryColor1, white} from "../../../styles/Colors";
import {TFunction} from "react-i18next";
import {ExtensionsListItem, SipTrunksListItem} from "../../../store/reducers/extensions/extensions/reducer";
import {CellProps, Column} from "react-table";
import {Permission} from "../../../store/types/Permission";
import PermissionLinkCell from "../../../components/DataTable/PermissionLinkCell";
import history from "../../../history";
import {getRoute, Routes} from "../../../routes/routes";
import ExtensionDIDNumber from "../../../components/Extensions/ExtensionDIDNumber";
import EmptyRowContent from "../../../components/DataTable/EmptyRowContent";
import React from "react";
import {convertServiceFeaturesToIconsFeatures} from "../../../utils/extensions/convertServiceFeaturesToIconsFeatures";
import {ExtensionServices} from "../../../components/Extensions/ExtensionServices";
import {FetchDataStatus} from "../../../store/reducers/autoAttendants/reducer";
import BarLoader from "../../../components/BarLoader/BarLoader";
import StatusItem from "../../../components/Extensions/StatusItem";
import PermissionProvider from "../../../components/PermissionProvider/PermissionProvider";
import CustomizedIconButton from "../../../components/IconButton/IconButton";
import {ReactComponent as CallHistoryIcon} from "../../../assets/call_history.svg";
import {Edit} from "@material-ui/icons";
import CallsCounterCell from "../../../components/Calls/CallsCounterCell";
import ActiveCalls from "../../../components/Calls/ActiveCalls";
import {CallProcessingModeName, CallProcessingModeNameMapping} from "../../../components/Calls/ModeCell";

export const useStyles = makeStyles(() => ({
    idHeader: {
        paddingLeft: 40
    },
    mainContainer: {
        flexDirection: 'column',
        height: '100vh',
        display: 'flex',

        '& .MuiButton-label': {
            color: white,
        },
        flex: 1,
        position: 'relative',
        background: Colors.SmokeBackground,
    },
    scrollable: {
        overflowY: 'auto',
        flex: 1,
        padding: '48px 96px',
        '& .MuiTableContainer-root': {
            width: 'unset !important'
        }
    },

    content: {
        maxWidth: 1040,
    },

    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
    },

    switchMode: {
        display: 'flex',
        marginTop: 10,
    },
    titleContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginRight: 25,
        '& h2': {
            fontSize: 32,
            lineHeight: '24px',
            marginBlockEnd: 8,
            marginBlockStart: 4,
            fontWeight: 500,
        },
        '& p': {
            fontSize: 14,
            lineHeight: '16px',
            opacity: 0.6,
            marginBlockStart: 0,
        },
    },
    filters: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: -65,
    },

    processableSearchIcon: {
        marginTop: 10,
        marginRight: 17,
        marginLeft: 17,
        cursor: 'pointer',
    },

    headerRow: {
        textTransform: 'uppercase',
        border: 0,
    },

    headerCell: {
        fontWeight: 700,
        fontSize: 12,
        backgroundColor: lightGray,
        height: '60px',
    },
    row: {
        borderBottom: 0,
    },
    filterSeparation: {
        marginRight: 18,
        flex: 1,
        maxWidth: '390px',
        background: 'white',
    },
    primaryModalButton: {
        '& .MuiButton-label': {
            color: Colors.Primary,
        },
    },
    extensionIdLink: {
        color: secondaryColor1,
        textDecoration: 'none',
        display: 'flex',
        justifyContent: 'center',
        flex: 1,
    },
    email: {
        fontSize: 14,
        opacity: 0.6,
    },
    actionsButtonsContainer: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
    },

    actionHeader: {textAlign: 'center'},
}));


export const generateColumns = (
    t: TFunction<string>,
    refreshStatus: (i_account: number) => void,
    showCallHistory: (item: SipTrunksListItem) => void,
    classes: ReturnType<typeof useStyles>,
): Column<SipTrunksListItem>[] => {
    return [
        {
            Header: (
                <div className={classes.idHeader}>
                    {t<string>('screens:sipTrunks.trunkId')}
                </div>
            ),
            accessor: 'id',
            width: 1.5,
            Cell: function Cell(params) {

                const permission = Permission.SipTrunks.SipTrunksDetails.value;
                return (
                    <PermissionLinkCell
                        text={params.row.original.id}
                        onClick={() =>
                            history.push(
                                getRoute(Routes.PureSipTrunkDetails, {id: params.row.original.id}),
                            )
                        }
                        className={classes.idHeader}
                        permissions={permission}
                    />
                );
            },
        },
        {
            Header: t<string>('screens:extensions.didNumber'),
            accessor: 'numbers',
            width: 1.2,

            Cell: function Cell(params) {
                const numbers = (
                    params.row.original.account_info?.alias_did_number_list ||
                    []
                ).map((v) => v.did_number);

                if (numbers.length > 0) {
                    return (
                        <ExtensionDIDNumber
                            showFooter={false}
                            extNumbers={numbers}
                        />
                    );
                }
                return <EmptyRowContent/>;
            },
        },
        {
            Header: t<string>('screens:sipTrunks.servicesAndCalls'),
            accessor: (row) => row.account_info!.email,
            width: 2,
            id: '3',
            Cell: function Cell(
                params: React.PropsWithChildren<CellProps<SipTrunksListItem, string[] | undefined>>,
            ) {
                const mode = params.row.original.callProcessingModeInfo?.name;
                const services = convertServiceFeaturesToIconsFeatures(
                    params.row.original.serviceFeatures || [],
                    params.row.original.mainService,
                    mode,
                );

                const modeToDisplay =
                    mode == CallProcessingModeName.Normal
                        ? CallProcessingModeNameMapping.Normal
                        : mode;

                return (
                    <div>
                        <ExtensionServices
                            services={services}
                            mode={modeToDisplay}
                        />
                        <CallsCounterCell calls={params.row.original.calls}/>
                    </div>
                );
            },
        },
        {
            Header: t<string>('screens:extensions.statusAndDevice'),
            //@ts-ignore
            accessor: 'status',
            width: 1.4,
            id: '4',
            Cell: function Cell(
                params: React.PropsWithChildren<CellProps<ExtensionsListItem, string[] | undefined>>,
            ) {
                const {
                    sip_status,
                    blocked,
                    sip_agent,
                    bill_status
                } = params.row.original.account_info!;

                const {fetchSipStatus, i_account} = params.row.original;
                return fetchSipStatus == FetchDataStatus.InProgress ? (
                    <BarLoader
                        dataQa={'sip-status-refresh-bar'}
                        dataTestId={'sip-status-refresh-bar'}
                    />
                ) : (
                    <StatusItem
                        blocked={blocked}
                        sip_status={sip_status!}
                        fetchData={() => refreshStatus(i_account)}
                        device={sip_agent}
                        billStatus={bill_status}
                    />
                );
            },
        },

        {
            Header: t<string>('screens:sipTrunks.activeCalls'),
            accessor: 'calls',
            width: .9,
            Cell: function Cell(params) {
                const calls = params.row.original.activeCalls;
                const filtered = calls.filter(item => item.count != 0);

                if (filtered.length) {
                    return <ActiveCalls data={filtered}/>
                }

                return <EmptyRowContent/>;
            }
        },
        {
            Header: (
                <div className={classes.actionHeader}>
                    {t<string>('common:actions')}
                </div>
            ),
            accessor: 'i_account',
            width: .8,
            Cell: function Cell(params) {
                return (
                    <div className={classes.actionsButtonsContainer}>
                        <PermissionProvider
                            permission={
                                Permission.SipTrunks.ViewCallHistory.value
                            }
                            skipReadOnly
                        >
                            <CustomizedIconButton
                                onClick={() =>
                                    showCallHistory(params.row.original)
                                }
                                tooltipText={t<string>(
                                    'screens:extensions.viewCallHistory',
                                )}
                                dataTestId="call-list-item-button"
                                dataQa="call-list-item-button"
                            >
                                <CallHistoryIcon/>
                            </CustomizedIconButton>
                        </PermissionProvider>

                        <PermissionProvider
                            permission={
                                Permission.SipTrunks.SipTrunksDetails.value
                            }
                            skipReadOnly
                        >
                            <CustomizedIconButton
                                onClick={() =>
                                    history.push(
                                        getRoute(Routes.PureSipTrunkDetails, {id: params.row.original.id}),
                                    )
                                }
                                tooltipText={t<string>('common:edit')}
                                dataTestId="edit-list-item-button"
                                dataQa="edit-list-item-button"
                            >
                                <Edit htmlColor={Colors.ListIconColor}/>
                            </CustomizedIconButton>
                        </PermissionProvider>
                    </div>
                );
            },
        },
    ];
};

export const mockItem = {
    "id": "27100000009",
    "numbers": [],
    "i_account": 78678,
    "i_customer": 7031,
    "account_info": {
        "sip_status": 0,
        "blocked": "N",
        "i_account": 78678,
        "alias_did_number_list": [],
        "bill_status": "O"
    },
    "serviceFeatures": [
        {
            "locked": 1,
            "flag_value": "~",
            "effective_flag_value": "Y",
            "name": "unified_messaging",
            "attributes": [
                {
                    "effective_values": [
                        "10"
                    ],
                    "name": "mailbox_limit",
                    "values": [
                        "10"
                    ]
                },
                {
                    "effective_values": [
                        "N"
                    ],
                    "name": "fax_only_mode",
                    "values": [
                        "N"
                    ]
                },
                {
                    "effective_values": [
                        "Voicemail Inbox"
                    ],
                    "name": "um_link",
                    "values": [
                        "Voicemail Inbox"
                    ]
                }
            ],
            "locks": [
                "user"
            ]
        },
        {
            "locked": 0,
            "flag_value": "/",
            "effective_flag_value": "N",
            "name": "sip_static_contact",
            "attributes": [
                {
                    "effective_values": [
                        "N"
                    ],
                    "name": "use_tcp",
                    "values": [
                        "N"
                    ]
                },
                {
                    "effective_values": [
                        null
                    ],
                    "name": "user",
                    "values": [
                        null
                    ]
                },
                {
                    "effective_values": [
                        null
                    ],
                    "name": "port",
                    "values": [
                        null
                    ]
                },
                {
                    "effective_values": [
                        null
                    ],
                    "name": "host",
                    "values": [
                        null
                    ]
                }
            ],
            "locks": []
        },
        {
            "locked": 0,
            "flag_value": "Y",
            "effective_flag_value": "Y",
            "name": "voice_fup",
            "attributes": [
                {
                    "effective_values": [
                        null
                    ],
                    "name": "max_calls_in",
                    "values": [
                        null
                    ]
                },
                {
                    "effective_values": [
                        null
                    ],
                    "name": "max_calls_out",
                    "values": [
                        null
                    ]
                },
                {
                    "effective_values": [
                        "30"
                    ],
                    "name": "max_calls",
                    "values": [
                        "30"
                    ]
                },
                {
                    "effective_values": [
                        null
                    ],
                    "name": "max_calls_fwd",
                    "values": [
                        null
                    ]
                }
            ],
            "locks": []
        },
        {
            "locked": 0,
            "flag_value": "~",
            "effective_flag_value": "Y",
            "name": "call_recording",
            "attributes": [
                {
                    "effective_values": [
                        null
                    ],
                    "name": "divider_1",
                    "values": [
                        null
                    ]
                },
                {
                    "effective_values": [
                        "0"
                    ],
                    "name": "call_recording_out",
                    "values": [
                        "0"
                    ]
                },
                {
                    "effective_values": [
                        "0"
                    ],
                    "name": "call_recording_in",
                    "values": [
                        "0"
                    ]
                },
                {
                    "effective_values": [
                        "0"
                    ],
                    "name": "call_recording_redirect",
                    "values": [
                        "0"
                    ]
                },
                {
                    "effective_values": [
                        null
                    ],
                    "name": "divider_2",
                    "values": [
                        null
                    ]
                },
                {
                    "effective_values": [
                        "Y"
                    ],
                    "name": "on_demand",
                    "values": [
                        "Y"
                    ]
                },
                {
                    "effective_values": [
                        null
                    ],
                    "name": "divider_3",
                    "values": [
                        null
                    ]
                },
                {
                    "effective_values": [
                        "N"
                    ],
                    "name": "mail_recording",
                    "values": [
                        "N"
                    ]
                },
                {
                    "effective_values": [
                        "N"
                    ],
                    "name": "audible_notification",
                    "values": [
                        "N"
                    ]
                },
                {
                    "effective_values": [
                        null
                    ],
                    "name": "divider_4",
                    "values": [
                        null
                    ]
                },
                {
                    "effective_values": [
                        "N"
                    ],
                    "name": "hide_recordings",
                    "values": [
                        "N"
                    ]
                }
            ],
            "locks": []
        },
        {
            "locked": 1,
            "flag_value": "^",
            "effective_flag_value": "Y",
            "name": "call_barring",
            "attributes": [
                {
                    "effective_values": [
                        "1105",
                        "1110",
                        "1119",
                        "1167"
                    ],
                    "name": "call_barring_rules",
                    "values": [
                        "1105",
                        "1110",
                        "1119",
                        "1167"
                    ]
                }
            ],
            "locks": [
                "user"
            ]
        },
        {
            "locked": 0,
            "flag_value": "~",
            "effective_flag_value": "Y",
            "name": "call_processing",
            "attributes": [
                {
                    "effective_values": [
                        "N"
                    ],
                    "name": "pbx_management",
                    "values": [
                        "N"
                    ]
                },
                {
                    "effective_values": [
                        "*62"
                    ],
                    "name": "cs_bulk_management",
                    "values": [
                        "*62"
                    ]
                },
                {
                    "effective_values": [
                        "*61"
                    ],
                    "name": "cs_individual_management",
                    "values": [
                        "*61"
                    ]
                }
            ],
            "locks": []
        }
    ],
    "fetchDIDNumbers": 0,
    "fetchSipStatus": 0,
    "calls": [
        {
            "name": "maxCallsIn",
            "value": null,
            "translation": "incoming"
        },
        {
            "name": "maxCallsOut",
            "value": null,
            "translation": "outgoing"
        },
        {
            "name": "maxCalls",
            "value": "30",
            "translation": "simultaneous"
        },
        {
            "name": "maxCallsFwd",
            "value": null,
            "translation": "forwarded"
        }
    ],
    "activeCalls": [
        {
            "name": "forwarded",
            "count": 0
        },
        {
            "name": "incoming",
            "count": 0
        },
        {
            "name": "outgoing",
            "count": 0
        },
        {
            "name": "simultaneous",
            "count": 0
        }
    ]
}